interface BadgeProps {
  children: React.ReactNode;
  variant?: 'success' | 'warning' | 'danger' | 'info' | 'gray' | 'primary';
  size?: 'sm' | 'md';
  dot?: boolean;
}

export default function Badge({
  children,
  variant = 'gray',
  size = 'md',
  dot = false,
}: BadgeProps) {
  const variantStyles = {
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    danger: 'bg-red-100 text-red-800',
    info: 'bg-blue-100 text-blue-800',
    gray: 'bg-gray-100 text-gray-800',
    primary: 'bg-brand/10 text-brand',
  };

  const sizeStyles = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-0.5 text-xs',
  };

  const dotStyles = {
    success: 'bg-green-500',
    warning: 'bg-yellow-500',
    danger: 'bg-red-500',
    info: 'bg-blue-500',
    gray: 'bg-gray-500',
    primary: 'bg-brand',
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 font-medium rounded-full ${variantStyles[variant]} ${sizeStyles[size]}`}
    >
      {dot && (
        <span className={`w-1.5 h-1.5 rounded-full ${dotStyles[variant]}`} />
      )}
      {children}
    </span>
  );
}

// Status-specific badges
export function StatusBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { variant: BadgeProps['variant']; label: string }> = {
    active: { variant: 'success', label: 'Active' },
    suspended: { variant: 'warning', label: 'Suspended' },
    banned: { variant: 'danger', label: 'Banned' },
    pending: { variant: 'info', label: 'Pending' },
    approved: { variant: 'success', label: 'Approved' },
    rejected: { variant: 'danger', label: 'Rejected' },
    under_review: { variant: 'warning', label: 'Under Review' },
    violation_confirmed: { variant: 'danger', label: 'Violation' },
    no_violation: { variant: 'gray', label: 'No Violation' },
    dismissed: { variant: 'gray', label: 'Dismissed' },
    fake_confirmed: { variant: 'danger', label: 'Fake Confirmed' },
    legitimate_review: { variant: 'success', label: 'Legitimate' },
    succeeded: { variant: 'success', label: 'Succeeded' },
    failed: { variant: 'danger', label: 'Failed' },
    refunded: { variant: 'info', label: 'Refunded' },
    partially_refunded: { variant: 'warning', label: 'Partial Refund' },
  };

  const config = statusConfig[status] || { variant: 'gray', label: status };

  return <Badge variant={config.variant} dot>{config.label}</Badge>;
}

export function TierBadge({ tier }: { tier: string }) {
  const tierConfig: Record<string, BadgeProps['variant']> = {
    free: 'gray',
    starter: 'info',
    professional: 'primary',
    enterprise: 'success',
  };

  return (
    <Badge variant={tierConfig[tier] || 'gray'}>
      {tier.charAt(0).toUpperCase() + tier.slice(1)}
    </Badge>
  );
}

export function SeverityBadge({ severity }: { severity: string }) {
  const severityConfig: Record<string, BadgeProps['variant']> = {
    minor: 'info',
    moderate: 'warning',
    severe: 'danger',
  };

  return (
    <Badge variant={severityConfig[severity] || 'gray'}>
      {severity.charAt(0).toUpperCase() + severity.slice(1)}
    </Badge>
  );
}

export function PriorityBadge({ priority }: { priority: number }) {
  if (priority >= 3) {
    return <Badge variant="danger">High Priority</Badge>;
  }
  if (priority >= 2) {
    return <Badge variant="warning">Medium</Badge>;
  }
  return <Badge variant="gray">Low</Badge>;
}
